import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { 
  FileText, 
  PlayCircle, 
  Plus, 
  Search, 
  Clock,
  CheckCircle2,
  AlertCircle,
  MoreHorizontal
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StartRunDialog } from '@/components/run/StartRunDialog';

interface LayoutContext {
  user: { id: string };
  organizationId: string;
}

interface SOP {
  id: string;
  title: string;
  description: string | null;
  status: string;
  tags: string[];
  step_count: number;
  estimated_duration_minutes: number | null;
  created_at: string;
}

interface Run {
  id: string;
  name: string;
  status: string;
  sop_title: string | null;
  progress_percent: number;
  due_at: string | null;
  created_at: string;
}

export default function Library() {
  const { user, organizationId } = useOutletContext<LayoutContext>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('playbooks');
  const [searchQuery, setSearchQuery] = useState('');
  const [sops, setSOPs] = useState<SOP[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [startRunDialogOpen, setStartRunDialogOpen] = useState(false);
  const [selectedSOP, setSelectedSOP] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    fetchData();
  }, [organizationId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch SOPs
      const { data: sopData, error: sopError } = await supabase
        .from('sop_templates')
        .select(`
          id, title, description, status, tags, estimated_duration_minutes, created_at,
          sop_steps(count)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (sopError) throw sopError;

      setSOPs((sopData || []).map(s => ({
        id: s.id,
        title: s.title,
        description: s.description,
        status: s.status,
        tags: s.tags || [],
        step_count: s.sop_steps?.[0]?.count || 0,
        estimated_duration_minutes: s.estimated_duration_minutes,
        created_at: s.created_at,
      })));

      // Fetch Runs
      const { data: runData, error: runError } = await supabase
        .from('sop_runs')
        .select(`
          id, name, status, due_at, created_at,
          sop_templates(title),
          sop_run_steps(status)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (runError) throw runError;

      setRuns((runData || []).map(r => {
        const totalSteps = r.sop_run_steps?.length || 0;
        const completedSteps = r.sop_run_steps?.filter((s: any) => s.status === 'COMPLETED').length || 0;
        return {
          id: r.id,
          name: r.name,
          status: r.status,
          sop_title: r.sop_templates?.title || null,
          progress_percent: totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0,
          due_at: r.due_at,
          created_at: r.created_at,
        };
      }));
    } catch (error) {
      console.error('Error fetching library data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSOPs = sops.filter(sop =>
    sop.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sop.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredRuns = runs.filter(run =>
    run.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    run.sop_title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <Badge variant="secondary">Draft</Badge>;
      case 'ACTIVE':
        return <Badge className="bg-success/10 text-success border-success/20">Active</Badge>;
      case 'ARCHIVED':
        return <Badge variant="outline">Archived</Badge>;
      case 'IN_PROGRESS':
        return <Badge className="bg-primary/10 text-primary border-primary/20">In Progress</Badge>;
      case 'COMPLETED':
        return <Badge className="bg-success/10 text-success border-success/20">Completed</Badge>;
      case 'CANCELLED':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleStartRun = (sopId: string, title: string) => {
    setSelectedSOP({ id: sopId, title });
    setStartRunDialogOpen(true);
  };

  return (
    <div className="flex flex-col h-full p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Library</h1>
        <Button
          variant="hero"
          size="sm"
          onClick={() => navigate('/app/sops')}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          New Playbook
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search playbooks and runs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="grid grid-cols-2 w-full max-w-xs">
          <TabsTrigger value="playbooks" className="gap-2">
            <FileText className="h-4 w-4" />
            Playbooks
          </TabsTrigger>
          <TabsTrigger value="runs" className="gap-2">
            <PlayCircle className="h-4 w-4" />
            Runs
          </TabsTrigger>
        </TabsList>

        {/* Playbooks Tab */}
        <TabsContent value="playbooks" className="flex-1 overflow-auto">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          ) : filteredSOPs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-8">
              <FileText className="h-16 w-16 text-muted-foreground/30 mb-6" />
              <h3 className="font-bold text-xl mb-2">No playbooks yet</h3>
              <p className="text-muted-foreground max-w-xs mb-8">
                Create your first playbook to standardize how work gets done.
              </p>
              <Button
                variant="hero"
                className="touch-target"
                onClick={() => navigate('/app/sops')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Playbook
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSOPs.map(sop => (
                <Card 
                  key={sop.id} 
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/app/sops/${sop.id}`)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-base">{sop.title}</CardTitle>
                        {sop.description && (
                          <CardDescription className="line-clamp-1 mt-1">
                            {sop.description}
                          </CardDescription>
                        )}
                      </div>
                      {getStatusBadge(sop.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center gap-4">
                        <span>{sop.step_count} steps</span>
                        {sop.estimated_duration_minutes && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {sop.estimated_duration_minutes}m
                          </span>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            handleStartRun(sop.id, sop.title);
                          }}>
                            <PlayCircle className="h-4 w-4 mr-2" />
                            Start Run
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/app/sops/${sop.id}`);
                          }}>
                            <FileText className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Runs Tab */}
        <TabsContent value="runs" className="flex-1 overflow-auto">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          ) : filteredRuns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-8">
              <PlayCircle className="h-16 w-16 text-muted-foreground/30 mb-6" />
              <h3 className="font-bold text-xl mb-2">No runs yet</h3>
              <p className="text-muted-foreground max-w-xs">
                Start a run from one of your playbooks to track progress.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRuns.map(run => (
                <Card 
                  key={run.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/app/runs/${run.id}`)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-base">{run.name}</CardTitle>
                        {run.sop_title && (
                          <CardDescription className="mt-1">
                            {run.sop_title}
                          </CardDescription>
                        )}
                      </div>
                      {getStatusBadge(run.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {/* Progress bar */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={cn(
                              'h-full transition-all',
                              run.status === 'COMPLETED' ? 'bg-success' : 'bg-primary'
                            )}
                            style={{ width: `${run.progress_percent}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{run.progress_percent}%</span>
                      </div>
                      
                      {/* Meta info */}
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>
                          {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}
                        </span>
                        {run.due_at && (
                          <span className={cn(
                            'flex items-center gap-1',
                            new Date(run.due_at) < new Date() && run.status !== 'COMPLETED' 
                              ? 'text-destructive' 
                              : ''
                          )}>
                            {new Date(run.due_at) < new Date() && run.status !== 'COMPLETED' ? (
                              <AlertCircle className="h-3 w-3" />
                            ) : (
                              <Clock className="h-3 w-3" />
                            )}
                            Due {formatDistanceToNow(new Date(run.due_at), { addSuffix: true })}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Start Run Dialog */}
      {selectedSOP && (
        <StartRunDialog
          open={startRunDialogOpen}
          onOpenChange={setStartRunDialogOpen}
          organizationId={organizationId}
          userId={user.id}
          onSuccess={() => {
            fetchData();
            setSelectedSOP(null);
          }}
        />
      )}
    </div>
  );
}
